import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BellOff, Bell, Plus, Trash2 } from 'lucide-react';
import type { AlertMetric } from '@neburst-bot/shared';
import { useAddAlert, useAlerts, useDeleteAlert } from '../api/hooks';
import {
  Button,
  Cell,
  EmptyState,
  PageHeader,
  Section,
  Segmented,
  TextInput,
} from '../components/ui';
import { getTg } from '../lib/tg';
import { useT, type TKey } from '../i18n';

const METRIC_KEYS: AlertMetric[] = ['traffic_pct', 'traffic_gb_used', 'instance_down', 'expires_in_days'];

const METRIC_LABEL_KEY: Record<AlertMetric, TKey> = {
  traffic_pct: 'metric.traffic_pct',
  traffic_gb_used: 'metric.traffic_gb_used',
  instance_down: 'metric.instance_down',
  expires_in_days: 'metric.expires_in_days',
};

const METRIC_FULL_KEY: Record<AlertMetric, TKey> = {
  traffic_pct: 'metric.full.traffic_pct',
  traffic_gb_used: 'metric.full.traffic_gb_used',
  instance_down: 'metric.full.instance_down',
  expires_in_days: 'metric.full.expires_in_days',
};

const METRIC_UNIT: Record<AlertMetric, string> = {
  traffic_pct: '%',
  traffic_gb_used: 'GB',
  instance_down: '',
  expires_in_days: 'd',
};

export function Alerts() {
  const navigate = useNavigate();
  const t = useT();
  const list = useAlerts();
  const add = useAddAlert();
  const del = useDeleteAlert();

  const [metric, setMetric] = useState<AlertMetric>('traffic_pct');
  const [threshold, setThreshold] = useState('80');

  const onDelete = (id: number) => {
    const tg = getTg();
    const fire = () => del.mutate(id);
    tg ? tg.showConfirm(t('alerts.confirmDelete'), (ok) => ok && fire()) : fire();
  };

  return (
    <>
      <header style={{ padding: '12px 8px 0' }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '8px 12px',
            color: 'var(--tg-button)',
            fontSize: 15,
            fontWeight: 500,
          }}
        >
          <ArrowLeft size={20} strokeWidth={2.4} />
          {t('common.back')}
        </button>
      </header>
      <PageHeader title={t('alerts.title')} subtitle={t('alerts.subtitle')} />

      <Section title={t('alerts.configured')}>
        {list.data && list.data.rules.length === 0 ? (
          <EmptyState
            icon={<BellOff size={28} />}
            title={t('alerts.empty.title')}
            description={t('alerts.empty.desc')}
          />
        ) : (
          (list.data?.rules ?? []).map((r, idx, arr) => (
            <Cell
              key={r.id}
              last={idx === arr.length - 1}
              leading={
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 10,
                    background: 'var(--tg-secondary-bg)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: r.enabled ? 'var(--warning)' : 'var(--tg-hint)',
                  }}
                >
                  <Bell size={18} />
                </div>
              }
              title={`${t(METRIC_FULL_KEY[r.metric] ?? 'metric.traffic_pct')} ≥ ${r.threshold}${METRIC_UNIT[r.metric] ?? ''}`}
              subtitle={`${t('alerts.cooldown', { n: r.cooldown_minutes })} · ${r.instance_uuid ? t('alerts.scopeOne') : t('alerts.scopeAll')}`}
              trailing={
                <button
                  onClick={() => onDelete(r.id)}
                  style={{
                    color: 'var(--danger)',
                    padding: 6,
                    display: 'inline-flex',
                  }}
                  aria-label={t('common.delete')}
                >
                  <Trash2 size={18} />
                </button>
              }
            />
          ))
        )}
      </Section>

      <Section title={t('alerts.add')} footer={t('alerts.addFooter')}>
        <div style={{ padding: 14, display: 'grid', gap: 12 }}>
          <Segmented<AlertMetric>
            value={metric}
            onChange={setMetric}
            options={METRIC_KEYS.map((k) => ({
              value: k,
              label: t(METRIC_LABEL_KEY[k]),
            }))}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ fontSize: 14, color: 'var(--tg-hint)', minWidth: 60 }}>
              {t('alerts.threshold')}
            </div>
            <div style={{ flex: 1 }}>
              <TextInput
                value={threshold}
                onChange={setThreshold}
                placeholder="100"
                inputMode="numeric"
                trailing={
                  <span style={{ fontSize: 14, color: 'var(--tg-hint)' }}>
                    {METRIC_UNIT[metric]}
                  </span>
                }
              />
            </div>
          </div>
          <Button
            disabled={add.isPending || Number.isNaN(Number(threshold))}
            loading={add.isPending}
            leading={!add.isPending && <Plus size={16} />}
            onClick={() =>
              add.mutate(
                {
                  instance_uuid: null,
                  metric,
                  threshold: Number(threshold),
                  cooldown_minutes: 60,
                  enabled: true,
                },
                {
                  onSuccess: () => getTg()?.HapticFeedback.notificationOccurred('success'),
                },
              )
            }
          >
            {t('alerts.addSubmit')}
          </Button>
        </div>
      </Section>
    </>
  );
}
